import { AsyncSearchableSelect } from '@/components/ui/async-searchable-select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getApiClient } from '@/services/apiClient';
import { AWB_STATUS_LABELS, PAYMENT_METHOD_LABELS } from '@/types/awb';
import { Filter, Package, X } from 'lucide-react';
import React from 'react';

export interface AWBFiltersState {
  search: string;
  status: string;
  customer: string;
  service: string;
  paymentMethod: string;
  dateFrom: string;
  dateTo: string;
  destination: string;
  weightMin: string;
  weightMax: string;
}

interface AWBFiltersProps {
  filters: AWBFiltersState;
  appliedFilters?: AWBFiltersState;
  onFilterChange: (key: keyof AWBFiltersState, value: string) => void;
  onClearFilters: () => void;
  onApplyFilters: () => void;
  onClearAppliedFilters?: (keys: (keyof AWBFiltersState)[]) => void;
  onFilterLabelChange?: (key: keyof AWBFiltersState, label: string) => void;
  filterLabels?: Partial<Record<keyof AWBFiltersState, string>>;
  appliedFilterLabels?: Partial<Record<keyof AWBFiltersState, string>>;
  isOpen: boolean;
  onToggleOpen: () => void;
  totalCount: number;
  loading: boolean;
  activeFilterCount?: number;
  hasPendingChanges?: boolean;
}

const AWBFilters: React.FC<AWBFiltersProps> = ({
  filters,
  appliedFilters,
  onFilterChange,
  onClearFilters,
  onApplyFilters,
  onClearAppliedFilters,
  onFilterLabelChange,
  filterLabels = {},
  appliedFilterLabels = {},
  isOpen,
  onToggleOpen,
  totalCount,
  loading,
  activeFilterCount,
  hasPendingChanges
}) => {
  const api = React.useMemo(() => getApiClient({ useWindowInstance: true }), []);
  const filterCount = activeFilterCount ?? 0;
  const hasActiveFilters = filterCount > 0;
  const canApplyFilters = hasPendingChanges === undefined ? true : hasPendingChanges;
  const canClearFilters = hasActiveFilters || (hasPendingChanges ?? true);
  const showPendingNotice = hasPendingChanges ?? false;
  const appliedState = appliedFilters ?? filters;

  const formatDateLabel = React.useCallback((value: string) => {
    if (!value) return '';
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return value;
    const safeDate = new Date(year, month - 1, day);
    if (Number.isNaN(safeDate.getTime())) return value;
    return safeDate.toLocaleDateString();
  }, []);

  const appliedFilterChips = React.useMemo(() => {
    const chips: Array<{
      label: string;
      value: string;
      clearKeys: (keyof AWBFiltersState)[];
    }> = [];

    const pushChip = (label: string, value: string, clearKeys: (keyof AWBFiltersState)[]) => {
      if (!value) return;
      chips.push({ label, value, clearKeys });
    };

    if (appliedState.search.trim()) {
      pushChip('Search', appliedState.search.trim(), ['search']);
    }

    if (appliedState.status && appliedState.status !== 'all') {
      pushChip('Status', AWB_STATUS_LABELS[appliedState.status] || appliedState.status, ['status']);
    }

    if (appliedState.customer) {
      pushChip(
        'Customer',
        appliedFilterLabels.customer || appliedState.customer,
        ['customer']
      );
    }

    if (appliedState.service) {
      pushChip(
        'Service',
        appliedFilterLabels.service || appliedState.service,
        ['service']
      );
    }

    if (appliedState.destination) {
      pushChip(
        'Destination',
        appliedFilterLabels.destination || appliedState.destination,
        ['destination']
      );
    }

    if (appliedState.paymentMethod && appliedState.paymentMethod !== 'all') {
      pushChip(
        'Payment',
        PAYMENT_METHOD_LABELS[appliedState.paymentMethod] || appliedState.paymentMethod,
        ['paymentMethod']
      );
    }

    if (appliedState.dateFrom || appliedState.dateTo) {
      const fromLabel = appliedState.dateFrom ? formatDateLabel(appliedState.dateFrom) : '';
      const toLabel = appliedState.dateTo ? formatDateLabel(appliedState.dateTo) : '';
      const value = fromLabel && toLabel
        ? `${fromLabel} → ${toLabel}`
        : fromLabel
          ? `From ${fromLabel}`
          : toLabel
            ? `Until ${toLabel}`
            : '';
      pushChip('Dates', value, ['dateFrom', 'dateTo']);
    }

    if (appliedState.weightMin || appliedState.weightMax) {
      const min = appliedState.weightMin.trim();
      const max = appliedState.weightMax.trim();
      const value = min && max
        ? `${min}–${max} kg`
        : min
          ? `≥ ${min} kg`
          : max
            ? `≤ ${max} kg`
            : '';
      pushChip('Weight', value, ['weightMin', 'weightMax']);
    }

    return chips;
  }, [appliedState, appliedFilterLabels, formatDateLabel]);

  const loadMasterDataOptions = React.useCallback(
    async (entity: string, inputValue: string, labelBuilder: (item: any) => string) => {
      try {
        const response = await api.get(`/api/master-data/${entity}`, {
          params: { search: inputValue, limit: 50 }
        });
        const items = response.data.data?.data || response.data.data || [];
        if (!Array.isArray(items)) return [];
        return items.map((item: any) => ({
          value: item._id,
          label: labelBuilder(item)
        }));
      } catch (error) {
        console.error(`Error fetching ${entity} options:`, error);
        return [];
      }
    },
    [api]
  );

  const loadCustomerOptions = React.useCallback(
    (inputValue: string) =>
      loadMasterDataOptions('customer', inputValue, (item) => {
        const name = item?.name || item?.code || 'Unknown';
        const code = item?.code ? ` (${item.code})` : '';
        return `${name}${code}`;
      }),
    [loadMasterDataOptions]
  );

  const loadServiceOptions = React.useCallback(
    (inputValue: string) =>
      loadMasterDataOptions('service', inputValue, (item) => {
        const name = item?.name || item?.code || 'Unknown';
        const code = item?.code ? ` (${item.code})` : '';
        return `${name}${code}`;
      }),
    [loadMasterDataOptions]
  );

  const loadCountryOptions = React.useCallback(
    (inputValue: string) =>
      loadMasterDataOptions('country', inputValue, (item) => {
        const name = item?.name || item?.code || 'Unknown';
        const code = item?.code ? ` (${item.code})` : '';
        return `${name}${code}`;
      }),
    [loadMasterDataOptions]
  );

  const handleEnterToApply = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    if (loading) return;
    if (hasPendingChanges === false) return;
    onApplyFilters();
  };

  const handleAsyncValueChange = (key: keyof AWBFiltersState) => (value: string) => {
    onFilterChange(key, value);
  };

  const handleAsyncOptionSelect = (key: keyof AWBFiltersState) => (option: { label: string } | null) => {
    if (!onFilterLabelChange) return;
    onFilterLabelChange(key, option?.label || '');
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Search & Filters</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={onToggleOpen}
          >
            <Filter className="mr-2 h-4 w-4" />
            {isOpen ? 'Hide' : 'Show'} Filters
            {activeFilterCount && activeFilterCount > 0 && (
              <span className="ml-2 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                {activeFilterCount}
              </span>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Quick Search */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Label htmlFor="search" className="text-sm font-medium">Search AWBs</Label>
            <Input
              id="search"
              placeholder="Search by AWB number… (Enter to apply)"
              value={filters.search}
              onChange={(e) => onFilterChange('search', e.target.value)}
              onKeyDown={handleEnterToApply}
              className="mt-1"
              disabled={loading}
            />
          </div>
          <div className="w-full sm:w-48">
            <Label htmlFor="status" className="text-sm font-medium">Status</Label>
            <Select value={filters.status} onValueChange={(value) => onFilterChange('status', value)} disabled={loading}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {Object.entries(AWB_STATUS_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Advanced Filters */}
        {isOpen && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t">
            <div>
              <Label htmlFor="customer" className="text-sm font-medium">Customer</Label>
              <AsyncSearchableSelect
                loadOptions={loadCustomerOptions}
                value={filters.customer}
                onValueChange={handleAsyncValueChange('customer')}
                onOptionSelect={handleAsyncOptionSelect('customer')}
                placeholder="Search customer..."
                searchPlaceholder="Type customer name..."
                emptyMessage="No customers found"
                className="mt-1"
                initialLabel={filterLabels.customer}
                disabled={loading}
              />
            </div>
            <div>
              <Label htmlFor="service" className="text-sm font-medium">Service</Label>
              <AsyncSearchableSelect
                loadOptions={loadServiceOptions}
                value={filters.service}
                onValueChange={handleAsyncValueChange('service')}
                onOptionSelect={handleAsyncOptionSelect('service')}
                placeholder="Search service..."
                searchPlaceholder="Type service name..."
                emptyMessage="No services found"
                className="mt-1"
                initialLabel={filterLabels.service}
                disabled={loading}
              />
            </div>
            <div>
              <Label htmlFor="destination" className="text-sm font-medium">Destination</Label>
              <AsyncSearchableSelect
                loadOptions={loadCountryOptions}
                value={filters.destination}
                onValueChange={handleAsyncValueChange('destination')}
                onOptionSelect={handleAsyncOptionSelect('destination')}
                placeholder="Search destination country..."
                searchPlaceholder="Type country name..."
                emptyMessage="No countries found"
                className="mt-1"
                initialLabel={filterLabels.destination}
                disabled={loading}
              />
            </div>
            <div>
              <Label htmlFor="paymentMethod" className="text-sm font-medium">Payment Method</Label>
              <Select
                value={filters.paymentMethod}
                onValueChange={(value) => onFilterChange('paymentMethod', value)}
                disabled={loading}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="All Methods" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Methods</SelectItem>
                  {Object.entries(PAYMENT_METHOD_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="dateFrom" className="text-sm font-medium">Date From</Label>
              <Input
                id="dateFrom"
                type="date"
                value={filters.dateFrom}
                onChange={(e) => onFilterChange('dateFrom', e.target.value)}
                onKeyDown={handleEnterToApply}
                className="mt-1"
                disabled={loading}
              />
            </div>
            <div>
              <Label htmlFor="dateTo" className="text-sm font-medium">Date To</Label>
              <Input
                id="dateTo"
                type="date"
                value={filters.dateTo}
                onChange={(e) => onFilterChange('dateTo', e.target.value)}
                onKeyDown={handleEnterToApply}
                className="mt-1"
                disabled={loading}
              />
            </div>
            <div>
              <Label htmlFor="weightMin" className="text-sm font-medium">Min Weight (KG)</Label>
              <Input
                id="weightMin"
                type="number"
                placeholder="0 (Enter to apply)"
                value={filters.weightMin}
                onChange={(e) => onFilterChange('weightMin', e.target.value)}
                onKeyDown={handleEnterToApply}
                className="mt-1"
                disabled={loading}
              />
            </div>
            <div>
              <Label htmlFor="weightMax" className="text-sm font-medium">Max Weight (KG)</Label>
              <Input
                id="weightMax"
                type="number"
                placeholder="1000 (Enter to apply)"
                value={filters.weightMax}
                onChange={(e) => onFilterChange('weightMax', e.target.value)}
                onKeyDown={handleEnterToApply}
                className="mt-1"
                disabled={loading}
              />
            </div>
          </div>
        )}

        {/* Enhanced Filter Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-4 border-t">
          <div className="text-sm text-muted-foreground flex flex-wrap items-center gap-2">
            <Package className="h-4 w-4" />
            <span>{totalCount} AWB{totalCount !== 1 ? 's' : ''} found</span>
            {hasActiveFilters && (
              <span className="ml-2 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                {filterCount} filter{filterCount !== 1 ? 's' : ''} applied
              </span>
            )}
            {showPendingNotice && (
              <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                Filters updated
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onClearFilters}
              disabled={loading || !canClearFilters}
            >
              Clear Filters
            </Button>
            <Button
              size="sm"
              onClick={onApplyFilters}
              disabled={loading || !canApplyFilters}
            >
              Apply Filters
            </Button>
          </div>
        </div>

        {appliedFilterChips.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed bg-muted/30 p-3">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Applied filters
            </span>
            {appliedFilterChips.map((chip, index) => (
              <Badge key={`${chip.label}-${index}`} variant="secondary" className="gap-1 pr-1 text-xs">
                <span className="max-w-[220px] truncate" title={`${chip.label}: ${chip.value}`}>
                  {chip.label}: {chip.value}
                </span>
                {onClearAppliedFilters && (
                  <button
                    type="button"
                    onClick={() => onClearAppliedFilters(chip.clearKeys)}
                    className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/70"
                    aria-label={`Clear ${chip.label} filter`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AWBFilters;
